import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Home, 
  Users, 
  Settings, 
  LogOut, 
  Moon, 
  Sun,
  ChevronLeft,
  ChevronRight,
  Shield,
  Activity,
  UserCog,
  ToggleLeft,
  Ticket,
  PanelLeftClose,
  PanelLeftOpen,
  User,
  Building2,
  Inbox,
  Contact
} from 'lucide-react';
import NotificationBell from './NotificationBell';

const MainLayout = ({ user, onLogout }) => {
  const { theme, setTheme, hasFeatureAccess } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Sidebar state - remember preference in localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', sidebarCollapsed);
  }, [sidebarCollapsed]);

  const isAdmin = user.role === 'admin';

  // Helper: Check if user has admin-level permission on a feature
  const hasFeatureAdmin = (featureId) => {
    if (isAdmin) return true;
    return user.accessible_features?.some(
      f => f.id === featureId && f.permission_level === 'admin'
    );
  };

  // Build navigation based on user's accessible features
  const navItems = [];
  
  if (hasFeatureAccess('intranet')) {
    navItems.push({
      id: 'intranet',
      label: 'Intranet',
      icon: Home,
      path: '/',
      paths: ['/', '/intranet']
    });
  }
  
  if (hasFeatureAccess('tischplan')) {
    navItems.push({
      id: 'tischplan',
      label: 'Tischplan',
      icon: Users,
      path: '/tischplan',
      paths: ['/tischplan', '/events', '/tischvorlagen']
    });
  }
  
  if (hasFeatureAccess('tickets')) {
    navItems.push({
      id: 'tickets',
      label: 'Tickets',
      icon: Ticket,
      path: '/tickets',
      paths: ['/tickets']
    });
  }
  
  if (hasFeatureAccess('logenbelegung')) {
    navItems.push({
      id: 'logenbelegung',
      label: 'Logenbelegung',
      icon: Building2,
      path: '/logenbelegung',
      paths: ['/logenbelegung']
    });
  }
  
  if (hasFeatureAccess('anfrageportal')) {
    navItems.push({
      id: 'anfrageportal',
      label: 'Anfrageportal',
      icon: Inbox,
      path: '/anfragen',
      paths: ['/anfragen']
    });
  }

  if (hasFeatureAccess('kontaktverwaltung')) {
    navItems.push({
      id: 'kontaktverwaltung',
      label: 'Kontakte',
      icon: Contact,
      path: '/kontakte',
      paths: ['/kontakte']
    });
  }

  // Admin navigation items - built dynamically
  const adminItems = [];
  
  // Global admin items: only for role=admin
  if (isAdmin) {
    adminItems.push(
      { id: 'users', label: 'Nutzerverwaltung', icon: UserCog, path: '/admin/users' },
      { id: 'features', label: 'Features', icon: ToggleLeft, path: '/admin/features' },
    );
  }
  
  // Feature-specific admin items: for role=admin OR feature-level admin permission
  if (hasFeatureAdmin('tickets')) {
    adminItems.push({ id: 'admin-tickets', label: 'Ticket-Verwaltung', icon: Ticket, path: '/admin/tickets' });
  }
  if (hasFeatureAdmin('logenbelegung')) {
    adminItems.push({ id: 'admin-logen', label: 'Logen-Verwaltung', icon: Building2, path: '/admin/logen' });
  }
  if (hasFeatureAdmin('anfrageportal')) {
    adminItems.push({ id: 'admin-anfragen', label: 'Anfrage-Verwaltung', icon: Inbox, path: '/admin/anfragen' });
  }
  if (hasFeatureAdmin('kontaktverwaltung')) {
    adminItems.push({ id: 'admin-kontakte', label: 'Kontakt-Verwaltung', icon: Contact, path: '/admin/kontakte' });
  }
  
  // Audit-Logs: only for role=admin
  if (isAdmin) {
    adminItems.push({ id: 'audit', label: 'Audit-Logs', icon: Activity, path: '/admin/audit-logs' });
  }

  const isActive = (item) => {
    if (item.paths) {
      return item.paths.some(p => 
        p === '/' ? location.pathname === '/' : location.pathname.startsWith(p)
      );
    }
    return location.pathname === item.path || location.pathname.startsWith(item.path);
  };

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  // Get user's department name if available
  const userDept = user.department?.name;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
      {/* Sidebar */}
      <aside 
        className={`
          fixed left-0 top-0 h-full z-40
          bg-white dark:bg-gray-950 
          border-r border-gray-200 dark:border-gray-800
          transition-all duration-300 ease-in-out
          flex flex-col
          ${sidebarCollapsed ? 'w-16' : 'w-64'}
        `}
      >
        {/* Logo / Brand */}
        <div className="h-16 flex items-center justify-center border-b border-gray-200 dark:border-gray-800 px-4">
          {sidebarCollapsed ? (
            <img 
              src="https://www.haie.de/wp-content/uploads/2021/11/del-logo-kec.svg" 
              alt="KEC" 
              className="h-10 w-10 object-contain"
            />
          ) : (
            <div className="flex items-center gap-3">
              <img 
                src="https://www.haie.de/wp-content/uploads/2021/11/del-logo-kec.svg" 
                alt="Kölner Haie" 
                className="h-10 w-10 object-contain"
              />
              <div className="flex flex-col">
                <span className="font-bold text-[#e30613]">Kölner Haie</span>
                <span className="text-xs text-gray-500">Intranet</span>
              </div>
            </div>
          )}
        </div>

        {/* Main Navigation */}
        <nav className="flex-1 py-4 px-2 overflow-y-auto">
          <div className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  data-testid={`nav-${item.id}`}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg
                    transition-all duration-200
                    ${active 
                      ? 'bg-[#e30613] text-white shadow-md' 
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }
                    ${sidebarCollapsed ? 'justify-center' : ''}
                  `}
                  title={sidebarCollapsed ? item.label : undefined}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-white' : ''}`} />
                  {!sidebarCollapsed && (
                    <span className="font-medium">{item.label}</span>
                  )}
                </Link>
              );
            })}
          </div>

          {/* Admin Section */}
          {adminItems.length > 0 && (
            <>
              <div className={`my-4 border-t border-gray-200 dark:border-gray-800 ${sidebarCollapsed ? 'mx-2' : 'mx-3'}`} />
              
              {!sidebarCollapsed && (
                <div className="px-3 mb-2">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                    <Shield className="w-3 h-3" />
                    Administration
                  </span>
                </div>
              )}
              
              <div className="space-y-1">
                {adminItems.map((item) => {
                  const Icon = item.icon;
                  const active = location.pathname === item.path;
                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      data-testid={`admin-${item.id}-link`}
                      className={`
                        flex items-center gap-3 px-3 py-2.5 rounded-lg
                        transition-all duration-200
                        ${active 
                          ? 'bg-purple-600 text-white shadow-md' 
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }
                        ${sidebarCollapsed ? 'justify-center' : ''}
                      `}
                      title={sidebarCollapsed ? item.label : undefined}
                    >
                      <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-white' : ''}`} />
                      {!sidebarCollapsed && (
                        <span className="font-medium text-sm">{item.label}</span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </>
          )}
        </nav>

        {/* User Section & Toggle */}
        <div className="border-t border-gray-200 dark:border-gray-800 p-2">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className={`
              w-full flex items-center gap-3 px-3 py-2 rounded-lg
              text-gray-700 dark:text-gray-300 
              hover:bg-gray-100 dark:hover:bg-gray-800
              transition-all duration-200
              ${sidebarCollapsed ? 'justify-center' : ''}
            `}
            title={theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          >
            {theme === 'dark' ? (
              <Sun className="w-5 h-5" />
            ) : (
              <Moon className="w-5 h-5" />
            )}
            {!sidebarCollapsed && (
              <span className="text-sm">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
            )}
          </button>

          {/* User Info */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`
                  w-full flex items-center gap-3 px-3 py-2 rounded-lg mt-1
                  text-gray-700 dark:text-gray-300 
                  hover:bg-gray-100 dark:hover:bg-gray-800
                  transition-all duration-200
                  ${sidebarCollapsed ? 'justify-center' : ''}
                `}
              >
                <div className="w-8 h-8 rounded-full bg-[#e30613] flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-white" />
                </div>
                {!sidebarCollapsed && (
                  <div className="flex-1 text-left overflow-hidden">
                    <p className="text-sm font-medium truncate">
                      {user.name || user.email?.split('@')[0]}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {user.role === 'super_admin' ? 'Super Admin' : 
                       user.role === 'admin' ? 'Admin' : 
                       user.role === 'hostess' ? 'Hostess' : 'Benutzer'}
                    </p>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={sidebarCollapsed ? 'center' : 'end'} className="w-56">
              <DropdownMenuLabel>
                <div>
                  <p className="font-medium">{user.name || 'Benutzer'}</p>
                  <p className="text-xs text-gray-500">{user.email}</p>
                  {userDept && (
                    <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                      <Building2 className="w-3 h-3" />
                      {userDept}
                    </p>
                  )}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                <LogOut className="w-4 h-4 mr-2" />
                Abmelden
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Collapse Toggle */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`
              w-full flex items-center gap-3 px-3 py-2 rounded-lg mt-1
              text-gray-500 
              hover:bg-gray-100 dark:hover:bg-gray-800
              transition-all duration-200
              ${sidebarCollapsed ? 'justify-center' : ''}
            `}
            title={sidebarCollapsed ? 'Menü ausklappen' : 'Menü einklappen'}
          >
            {sidebarCollapsed ? (
              <PanelLeftOpen className="w-5 h-5" />
            ) : (
              <>
                <PanelLeftClose className="w-5 h-5" />
                <span className="text-sm">Einklappen</span>
              </>
            )}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main 
        className={`
          flex-1 min-h-screen
          transition-all duration-300 ease-in-out
          ${sidebarCollapsed ? 'ml-16' : 'ml-64'}
        `}
      >
        {/* Top Bar (optional - for breadcrumbs or actions) */}
        <header className="h-16 bg-white dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 px-6 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-2">
            {/* Current page title could go here */}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Notification Bell - visible for all logged-in users */}
            <NotificationBell />

            {/* Mobile sidebar toggle for small screens */}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              {sidebarCollapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default MainLayout;
